<template>
  <div class="container py-4" style="position: relative;">
    <h2 class="mb-4">Send Push Notification</h2>

    <!-- Role filter radio buttons -->
    <div class="form-group mb-3">
      <label class="mr-3">Filter by Role:</label>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input"
          type="radio"
          id="roleAll"
          value=""
          v-model="selectedRole"
        />
        <label class="form-check-label" for="roleAll">All</label>
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input"
          type="radio"
          id="roleScout"
          value="scout"
          v-model="selectedRole"
        />
        <label class="form-check-label" for="roleScout">Scout</label>
      </div>
      <div class="form-check form-check-inline">
        <input
          class="form-check-input"
          type="radio"
          id="roleTalent"
          value="talent"
          v-model="selectedRole"
        />
        <label class="form-check-label" for="roleTalent">Talent</label>
      </div>
    </div>

    <!-- Notification Text -->
    <div class="form-group">
      <label for="notificationText">Notification Message</label>
      <textarea
        v-model="notificationText"
        id="notificationText"
        rows="3"
        class="form-control"
        placeholder="Type your notification here..."
      ></textarea>
    </div>

    <!-- Dropdown Checkbox List -->
    <div class="form-group mt-2" style="position: relative;">
      <label>Select Users to Notify</label>
      <div class="dropdown-wrapper">
        <button
          ref="dropdownButton"
          type="button"
          class="btn btn-outline-secondary dropdown-toggle custom-dropdown-button"
          @click.stop="toggleDropdown"
          :title="dropdownButtonTitle"
        >
          <span class="dropdown-button-text">{{ dropdownButtonTitle }}</span>
        </button>

        <div
          ref="dropdownMenu"
          v-show="dropdownOpen"
          class="dropdown-menu-custom p-2"
          @click.stop
        >
          <!-- Search input inside dropdown -->
          <input
            type="text"
            v-model="searchQuery"
            class="form-control mb-2"
            placeholder="Search users..."
            @input="onSearchInput"
            autocomplete="off"
          />

          <div class="form-check mb-2">
            <input
              type="checkbox"
              class="form-check-input"
              id="selectAll"
              v-model="selectAll"
              @change="toggleSelectAll"
            />
            <label class="form-check-label font-weight-bold" for="selectAll">
              Select All
            </label>
          </div>

          <div
            class="form-check"
            v-for="user in filteredUsers"
            :key="user.id"
            :title="user.name + ' (' + user.email + ')'"
          >
            <input
              type="checkbox"
              class="form-check-input"
              :id="'user_' + user.id"
              :value="user.id"
              v-model="selectedUsers"
              :disabled="selectAll"
            />
            <label class="form-check-label" :for="'user_' + user.id">
              {{ user.name }} ({{ user.email }})
            </label>
          </div>

          <div v-if="filteredUsers.length === 0" class="text-muted mt-2">
            No users found.
          </div>
        </div>
      </div>
    </div>

    <!-- Buttons -->
    <div class="mt-4 d-flex" style="gap: 10px;">
      <button
        class="btn btn-primary"
        :disabled="!canSend || isSending"
        @click="sendNotification"
      >
        {{ isSending ? "Sending..." : "Send Notification" }}
      </button>
      <button class="btn btn-secondary" @click="resetForm">Cancel</button>
    </div>

    <!-- Loading/Error display -->
    <div v-if="loading" class="mt-3 alert alert-warning">
      Loading users...
    </div>
    <div v-if="error" class="mt-3 alert alert-danger">
      Error: {{ error }}
    </div>

    <!-- ================= Notification List ================= -->
    <hr class="my-5" />
    <h3 class="mb-3">Notification History</h3>

    <div v-if="loadingNotifications" class="alert alert-info">
      Loading notifications...
    </div>
    <div v-if="notificationError" class="alert alert-danger">
      Error: {{ notificationError }}
    </div>

    <div v-if="!loadingNotifications && notifications.length === 0" class="text-muted">
      No notifications found.
    </div>

    <ul class="list-group" v-if="paginatedNotifications.length > 0">
      <li
        v-for="notif in paginatedNotifications"
        :key="notif.id"
        class="list-group-item d-flex flex-column"
      >
        <div class="d-flex justify-content-between align-items-center">
          <span class="font-weight-bold">{{ notif.message }}</span>
          <div class="text-right">
            <small class="text-muted d-block"> {{ notif.createdDate }}</small>
            <small class="text-muted d-block"> {{ notif.createdTime }}</small>
          </div>
        </div>
        <div class="mt-1">
          <small v-if="!!notif.sentToAll" class="badge badge-primary">Sent to All</small>
        </div>
      </li>
    </ul>

    <!-- Pagination Controls -->
    <div
      v-if="totalPages > 1"
      class="d-flex justify-content-between align-items-center mt-3"
    >
      <button
        class="btn btn-outline-primary"
        :disabled="currentPage === 1"
        @click="currentPage--"
      >
        « Previous
      </button>
      <span class="mx-2">Page {{ currentPage }} of {{ totalPages }}</span>
      <button
        class="btn btn-outline-primary"
        :disabled="currentPage === totalPages"
        @click="currentPage++"
      >
        Next »
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  name: "PushNotification",
  components: {
    ToastificationContent,
  },
  data() {
    return {
      users: [],
      selectedUsers: [],
      selectAll: false,
      notificationText: "",
      dropdownOpen: false,
      loading: false,
      error: null,
      isSending: false,
      searchQuery: "",
      selectedRole: "",

      // Notifications state
      notifications: [],
      loadingNotifications: false,
      notificationError: null,

      // Pagination
      currentPage: 1,
      perPage: 5,
    };
  },
  computed: {
    canSend() {
      return (
        this.notificationText.trim() !== "" &&
        (this.selectAll || this.selectedUsers.length > 0)
      );
    },
    dropdownButtonTitle() {
      if (this.selectAll) return "All users selected";
      if (this.selectedUsers.length > 0)
        return `${this.selectedUsers.length} user(s) selected`;
      return "Select Users";
    },
    filteredUsers() {
      let filtered = this.users;

      if (this.selectedRole) {
        filtered = filtered.filter((user) => user.role === this.selectedRole);
      }

      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (u) =>
            u.name.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
        );
      }

      return filtered;
    },
    paginatedNotifications() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.notifications.slice(start, start + this.perPage);
    },
    totalPages() {
      return Math.ceil(this.notifications.length / this.perPage);
    },
  },
  methods: {
    fetchUsers() {
      this.loading = true;
      this.error = null;
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Users", {
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
          },
        })
        .then((response) => {
          if (
            response.data.status === "success" &&
            Array.isArray(response.data.data)
          ) {
            this.users = response.data.data.map((user) => ({
              id: user.id,
              name: user.name,
              email: user.email,
              role: user.role || "",
            }));
            this.selectedUsers = [];
            this.selectAll = false;
            this.searchQuery = "";
            this.selectedRole = "";
          } else {
            this.error = "API did not return user list properly.";
          }
        })
        .catch((error) => {
          this.error = error.message || "Failed to fetch users.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
      if (!this.dropdownOpen) {
        this.searchQuery = "";
      }
    },
    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedUsers = [];
      }
    },
    sendNotification() {
      if (!this.canSend) return;

      const payload = {
        message: this.notificationText,
        sendToAll: this.selectAll,
      };

      if (!this.selectAll) {
        payload.userIds = this.selectedUsers;
      }

      this.isSending = true;

      axios
        .post(
          "https://geotalent-backend-production.up.railway.app/api/Notifications/SendAdminNotification",
          payload,
          {
            headers: {
              Authorization: "bearer " + this.$store.state.userData.token,
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          if (response.data.status === "success") {
            this.resetForm(true);

            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Notification Sent",
                icon: "SendIcon",
                variant: "success",
                text: response.data.message || "Notification sent successfully!",
              },
            });

            this.fetchNotifications();
          } else {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Error",
                icon: "AlertCircleIcon",
                variant: "danger",
                text: response.data.message || "Something went wrong.",
              },
            });
          }
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              icon: "AlertCircleIcon",
              variant: "danger",
              text:
                error.response?.data?.message ||
                "Failed to send notification.",
            },
          });
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    resetForm() {
      this.notificationText = "";
      this.selectedUsers = [];
      this.selectAll = false;
      this.dropdownOpen = false;
      this.searchQuery = "";
      this.selectedRole = "";
    },
    handleClickOutside(event) {
      const dropdownMenu = this.$refs.dropdownMenu;
      const dropdownButton = this.$refs.dropdownButton;

      if (
        this.dropdownOpen &&
        dropdownMenu &&
        dropdownButton &&
        !dropdownMenu.contains(event.target) &&
        !dropdownButton.contains(event.target)
      ) {
        this.dropdownOpen = false;
        this.searchQuery = "";
      }
    },
    onSearchInput() {},
    fetchNotifications() {
      this.loadingNotifications = true;
      this.notificationError = null;
      axios
        .get("https://geotalent-backend-production.up.railway.app/api/Notifications", {
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
          },
        })
        .then((response) => {
          if (response.data.status === "success" && Array.isArray(response.data.data)) {
            this.notifications = response.data.data.map((n) => {
              let dateObj = new Date(n.date || n.created_at || n.createdAt);
              return {
                id: n.id,
                message: n.message,
                sentToAll: n.sendToAll,
                userIds: n.userIds || [],
                createdDate: dateObj.toLocaleDateString(), // sirf date
                createdTime: dateObj.toLocaleTimeString(), // sirf time
              };
            });
            this.currentPage = 1;
          } else {
            this.notificationError = "API did not return notifications list properly.";
          }
        })
        .catch((error) => {
          this.notificationError = error.message || "Failed to fetch notifications.";
        })
        .finally(() => {
          this.loadingNotifications = false;
        });
    },
  },
  mounted() {
    this.fetchUsers();
    this.fetchNotifications();
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.handleClickOutside);
  },
};
</script>

<style scoped>
textarea {
  resize: none;
}

.dropdown-wrapper {
  position: relative;
  max-width: 350px;
  width: 100%;
}

.custom-dropdown-button {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 12px;
  width: 100%;
  font-size: 14px;
  background-color: white;
  color: black;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-dropdown-button::after {
  content: "";
  display: inline-block;
  margin-left: auto;
  vertical-align: middle;
  border-top: 5px solid;
  border-right: 5px solid transparent;
  border-left: 5px solid transparent;
}

.dropdown-menu-custom {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  max-width: 350px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 6px 15px 3px rgba(0, 0, 0, 0.06);
  padding: 8px 12px;
  font-size: 14px;
  z-index: 1050;
  background-color: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(100px);
  color: black;
}

.dropdown-menu-custom input[type="text"] {
  border-radius: 4px;
  border: 1px solid #ccc;
  padding: 6px 8px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.dropdown-menu-custom::-webkit-scrollbar {
  width: 6px;
}
.dropdown-menu-custom::-webkit-scrollbar-thumb {
  border-radius: 3px;
}

.form-check {
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-check label {
  font-size: 13px;
}

.form-check input[type="checkbox"] {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  accent-color: #0d6efd;
}

.form-check input[type="checkbox"]:disabled + label {
  color: #777;
} 

.form-group.mb-3 {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-check-inline {
  margin-right: 12px;
}
</style>
